import { useState } from 'react';
import { Box, Button, Typography, Tooltip, Alert, Divider } from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import LinkIcon from '@mui/icons-material/Link';
import { useLayoutPreferences } from '../../../../context/LayoutPreferencesContext';
import EmailUnlayerSection from './EmailUnlayerSection';
import InsertPersonalFieldsDialog from './InsertPersonalFieldsDialog';
import SmsPhoneMockup from './SmsPhoneMockup';
import SmsOfferCodeDialog from './SmsOfferCodeDialog';

interface SmsCreationSectionProps {
  offerCount?: number;
  offerTitles?: string[];
  onAddOffer?: () => void;
  onOfferUsed?: (offerIndex: number) => void;
  initialPayload?: Record<string, unknown> | null;
  onPayloadChange?: (payload: Record<string, unknown>) => void;
}

function SmsCreationSection({ offerCount = 1, offerTitles, onAddOffer, onOfferUsed, initialPayload, onPayloadChange }: SmsCreationSectionProps) {
  const { sidebarColor } = useLayoutPreferences();
  const [message, setMessage] = useState(() => (initialPayload?.message as string) ?? '');
  const [enrichi, setEnrichi] = useState(() => (initialPayload?.enrichi as boolean) ?? false);
  const [personalFieldsOpen, setPersonalFieldsOpen] = useState(false);
  const [offerDialogOpen, setOfferDialogOpen] = useState(false);

  const handleMessageChange = (value: string) => {
    setMessage(value);
    onPayloadChange?.({ type: 'sms', message: value, enrichi });
  };

  const handleEnrichiChange = (value: boolean) => {
    setEnrichi(value);
    onPayloadChange?.({ type: 'sms', message, enrichi: value });
  };

  const existingCount = Math.max(1, offerCount);
  const offerCodes = Array.from({ length: existingCount + 1 }, (_, i) => `#NUM_COUPON_${i}#`);

  const insertToken = (token: string) => {
    const newMessage = message + token;
    setMessage(newMessage);
    onPayloadChange?.({ type: 'sms', message: newMessage, enrichi });
    setOfferDialogOpen(false);
    const match = token.match(/#NUM_COUPON_(\d+)#/);
    if (match) {
      const idx = parseInt(match[1]);
      onOfferUsed?.(idx);
      if (idx >= offerCount) onAddOffer?.();
    }
  };

  const btnSx = {
    textTransform: 'none' as const,
    fontWeight: 500,
    fontSize: '0.82rem',
    borderColor: sidebarColor,
    color: sidebarColor,
    borderRadius: 1.5,
    '&:hover': { borderColor: sidebarColor, backgroundColor: `${sidebarColor}10` },
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          mb: 3,
          p: 1.5,
          backgroundColor: '#f8f9fa',
          border: '1px solid #e9ecef',
          borderRadius: 2,
        }}
      >
        <Tooltip title="Formater le texte">
          <Button variant="outlined" size="small" startIcon={<BuildIcon fontSize="small" />} sx={btnSx}>
            Outil Texte
          </Button>
        </Tooltip>
        <Tooltip title="Insérer un champ personnalisé">
          <Button
            variant="outlined"
            size="small"
            startIcon={<PersonAddAltIcon fontSize="small" />}
            onClick={() => setPersonalFieldsOpen(true)}
            sx={btnSx}
          >
            Champ de personnalisation
          </Button>
        </Tooltip>
        <Tooltip title="Insérer un code d'opération (offre)">
          <Button
            variant="outlined"
            size="small"
            startIcon={<ConfirmationNumberIcon fontSize="small" />}
            onClick={() => setOfferDialogOpen(true)}
            sx={btnSx}
          >
            Code opération
          </Button>
        </Tooltip>
      </Box>

      <SmsPhoneMockup
        message={message}
        enrichi={enrichi}
        onMessageChange={handleMessageChange}
        onEnrichiChange={handleEnrichiChange}
      />

      {enrichi && (
        <Box sx={{ mt: 4 }}>
          <Divider sx={{ mb: 3 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <LinkIcon sx={{ color: sidebarColor, fontSize: '1.1rem' }} />
            <Typography variant="body2" fontWeight={700}>
              Landing page du SMS enrichi
            </Typography>
          </Box>
          <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem' }}>
            Le token <strong>#SHORT_URL#</strong> sera automatiquement inséré dans votre message et pointera vers cette landing page.
          </Alert>
          <EmailUnlayerSection isLandingPage />
        </Box>
      )}

      <InsertPersonalFieldsDialog
        open={personalFieldsOpen}
        onClose={() => setPersonalFieldsOpen(false)}
        onInsert={token => insertToken(token)}
        channel="sms"
      />

      <SmsOfferCodeDialog
        open={offerDialogOpen}
        onClose={() => setOfferDialogOpen(false)}
        offerCodes={offerCodes}
        offerTitles={offerTitles}
        onSelect={insertToken}
      />
    </Box>
  );
}

export default SmsCreationSection;
